import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map as rxMap } from 'rxjs/operators';

import { Graticule, Map as OlMap, View } from 'ol';
import { transform } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { EventsKey } from 'ol/events';
import { Pixel } from 'ol/pixel';
import { Fill, Stroke, Text as OlText } from 'ol/style';

import MapConstants from 'src/app/components/carte/constants';
import { BlastPolygonsLayer } from 'src/app/components/carte/layers/BlastPolygoneLayer';
import { HoleLayer } from 'src/app/components/carte/layers/holeLayer';
import { Blast3DViewerLazyComponent } from 'src/app/components/blast3-dviewer-lazy/blast3-dviewer-lazy.component';
import { Blast, HoleService, HoleStatus } from 'src/app/services/hole.service';
import { HttpService } from 'src/app/services/http.service';
import { LoggerService } from 'src/app/services/logger.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { Site } from 'src/app/bo/site';
import { PolygonType } from 'src/app/bo/polygon/tirsPolygone';

import { SegmentedControlComponent, SegmentedOption } from '../segmented-control/segmented-control.component';
import { HoleCellFeature, HoleCellsLayer } from './hole-cells-layer';
import { ensureProjection, normalizeEpsgCode } from '../projection.utils';
import { BlastHolesStateService, HoleStatusFilter } from '../../blast-holes-state.service';
import { HolesMapMode } from '../../blast-holes.types';

const POLYGON_LAYER_Z        = 20;
const CELLS_LAYER_Z          = 25;
const HOLE_LAYER_Z           = 30;
const INITIAL_ZOOM           = 20;
/** Hole names are hidden when the view resolution (m/px) is above this value. */
const LABEL_HIDE_RESOLUTION  = 0.1;
const REFRESH_DEBOUNCE_MS    = 100;
/** Blast polygon types drawn under the holes: automatic-from-holes (0) and drilling contour (1). */
const BLAST_POLYGON_TYPES    = [PolygonType.AutomaticFromHoles, PolygonType.DrillingContour];
const BLAST_POLYGON_STYLE    = 4;

/**
 * Middle column of the Holes tab: the 2D OpenLayers blast map (holes + polygon + graticule)
 * with a "Top | 3D" toggle that swaps in the three.js viewer.
 * Reuses the legacy HoleLayer / BlastPolygonsLayer classes; the map instance survives
 * Top ↔ 3D toggles (only its target element is detached / re-attached).
 *
 * Design source : Figma 2544:670313 ("Container")
 */
@Component({
  selector:        'app-blast-holes-map',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports:         [TranslateModule, SegmentedControlComponent, Blast3DViewerLazyComponent],
  templateUrl:     './blast-holes-map.component.html',
  styleUrls:       ['./blast-holes-map.component.scss'],
})
export class BlastHolesMapComponent implements OnDestroy {
  protected readonly state = inject(BlastHolesStateService);

  private readonly http        = inject(HttpService);
  private readonly holeService = inject(HoleService);
  private readonly logger      = inject(LoggerService);
  private readonly userService = inject(UtilisateurService);
  private readonly translate   = inject(TranslateService);

  private readonly mapTarget = viewChild<ElementRef<HTMLDivElement>>('mapTarget');

  // ── Mode toggle options ───────────────────────────────────────────────────

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(rxMap(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  protected readonly modeOptions = computed<SegmentedOption<HolesMapMode>[]>(() => {
    this.lang();
    return [
      { id: 'top', label: this.translate.instant('HOLES_TAB.MAP_TOP') },
      { id: '3d',  label: this.translate.instant('HOLES_TAB.MAP_3D') },
    ];
  });

  private readonly viewer3d = viewChild<Blast3DViewerLazyComponent>('viewer3d');

  protected readonly panMoves = [
    { id: 'up',    dx:  0, dy:  1, label: 'HOLES_TAB.MOVE_UP' },
    { id: 'left',  dx: -1, dy:  0, label: 'HOLES_TAB.MOVE_LEFT' },
    { id: 'right', dx:  1, dy:  0, label: 'HOLES_TAB.MOVE_RIGHT' },
    { id: 'down',  dx:  0, dy: -1, label: 'HOLES_TAB.MOVE_DOWN' },
  ] as const;

  // ── OpenLayers state ──────────────────────────────────────────────────────

  private map: OlMap | null = null;
  private readonly view = new View();
  private holeLayer: HoleLayer | null = null;
  private polygonLayer: BlastPolygonsLayer | null = null;
  private cellsLayer: HoleCellsLayer | null = null;
  /** Canonical name ("EPSG:31370") of the site projection, whatever form `site.epsg` comes in. */
  private siteProjection: string | null = null;
  private renderedBlast: Blast | null = null;
  /** "siteId|EPSG:xxxx" the current layers were built for: a site change rebuilds them even for the same blast. */
  private renderedSiteKey: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private resolutionKey: EventsKey | null = null;
  private readonly isMapLoading$ = new BehaviorSubject<boolean>(false);
  private destroyed = false;
  /** Incremented by each syncMap call: a call resumed after an await bails out if a newer one started. */
  private syncId = 0;

  constructor() {
    // (Re)attach the map whenever the target element, the blast or the current site changes.
    effect(() => {
      const target = this.mapTarget()?.nativeElement ?? null;
      const blast  = this.state.blast();
      const site   = this.userService.currentSite();
      untracked(() => void this.syncMap(target, blast, site));
    });

    effect(() => {
      const holeId = this.state.selectedHoleId();
      untracked(() => this.applySelection(holeId));
    });

    effect(() => {
      const filter = this.state.statusFilter();
      untracked(() => this.applyStatusFilter(filter));
    });

    effect(() => {
      this.state.cellOverlay();
      this.state.holeCells();
      untracked(() => this.renderCells());
    });

    effect(() => {
      this.state.holeColors();
      this.state.holes();
      untracked(() => this.applyHoleColors());
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (this.resolutionKey) unByKey(this.resolutionKey);
    this.map?.setTarget(undefined);
    this.map = null;
  }

  // ── Template handlers ─────────────────────────────────────────────────────

  protected onModeChange(mode: string): void {
    this.state.mapMode.set(mode as HolesMapMode);
  }

  protected zoom(delta: number): void {
    if (!this.map) return;
    const current = this.view.getZoom() ?? INITIAL_ZOOM;
    this.view.animate({ zoom: current + delta, duration: 200 });
  }

  /** Moves the 3D camera one step along the screen axes. */
  protected pan3d(dx: number, dy: number): void {
    this.viewer3d()?.pan(dx, dy);
  }

  /** Reloads the 3D viewer holes (after an import); no-op in top view, the viewer reloads when mounted. */
  reload3d(): void {
    this.viewer3d()?.reload();
  }

  // ── Map lifecycle ─────────────────────────────────────────────────────────

  private async syncMap(target: HTMLDivElement | null, blast: Blast | null, site: Site | undefined): Promise<void> {
    const syncId = ++this.syncId;
    if (!target) {
      // 3D mode or not rendered yet: detach, keep the instance for a cheap re-attach.
      this.map?.setTarget(undefined);
      return;
    }

    const firstHole = blast?.BlastDesign?.[0];
    if (!blast || !firstHole) {
      // No blast (loading / error) or a blast without holes: wipe the previous blast's layers
      // instead of leaving them on screen.
      if (this.renderedBlast !== null) {
        this.resetLayers();
        this.renderedBlast = null;
      }
      return;
    }

    const siteProjection = normalizeEpsgCode(site?.epsg);
    if (!site || !siteProjection) {
      // Site not loaded yet (the effect reruns when it is) or without a projection: drop the previous layers.
      if (this.renderedBlast !== null) {
        this.resetLayers();
        this.renderedBlast = null;
      }
      return;
    }

    try {
      // The shell is reused across sites: register every site projection, not only the first one.
      // ensureProjection deduplicates, so an already-known code costs nothing.
      await ensureProjection(this.http, siteProjection);
      if (this.destroyed || syncId !== this.syncId) return;
      this.siteProjection = siteProjection;
      if (!this.map) {
        this.createMap(target);
      } else if (this.map.getTarget() !== target) {
        this.map.setTarget(target);
      }
    } catch (error) {
      console.error('BlastHolesMap: unable to initialise the map', error);
      this.logger.logError(error);
      // Don't leave the previous blast (possibly from another site) on screen.
      if (syncId === this.syncId && this.renderedBlast !== null) {
        this.resetLayers();
        this.renderedBlast = null;
      }
      return;
    }

    const siteKey = `${site.siteId}|${siteProjection}`;
    if (this.renderedBlast !== blast || this.renderedSiteKey !== siteKey) {
      this.resetLayers();
      this.addLayers(site, siteProjection, blast.tirId);
      // Centre on the first hole that has a planned position: a blast whose holes carry none
      // (positions not imported yet) keeps the current view instead of throwing here, which
      // would leave the layers empty and skip the refresh below.
      const anchor = blast.BlastDesign.find(hole =>
        Number.isFinite(hole.PositionPlannedXYZ?.x) && Number.isFinite(hole.PositionPlannedXYZ?.y),
      );
      if (anchor) this.centerOn(anchor.PositionPlannedXYZ.x, anchor.PositionPlannedXYZ.y, INITIAL_ZOOM);
      else console.warn('BlastHolesMap: no hole of this blast has a planned position, keeping the current view');
      this.renderedBlast = blast;
      this.renderedSiteKey = siteKey;
    }

    this.scheduleRefresh(0);
  }

  private createMap(target: HTMLDivElement): void {
    this.map = new OlMap({ target, view: this.view, controls: [], layers: [] });
    this.map.on('click', event => this.onMapClick(event.pixel));
    this.map.on('moveend', () => this.scheduleRefresh(REFRESH_DEBOUNCE_MS));
    this.resolutionKey = this.view.on('change:resolution', () => this.updateLabelVisibility()) as EventsKey;
  }

  private addLayers(site: Site, siteProjection: string, blastId: number): void {
    if (!this.map) return;
    this.polygonLayer = new BlastPolygonsLayer(this.map, site.siteId, this.http, POLYGON_LAYER_Z);
    this.cellsLayer   = new HoleCellsLayer(this.map, CELLS_LAYER_Z);
    // The site EPSG lets the layer place holes from their projected XYZ position, like the cells.
    this.holeLayer    = new HoleLayer(this.map, this.holeService, this.logger, HOLE_LAYER_Z, siteProjection.split(':')[1]);
    this.map.addLayer(this.createGraticule(siteProjection));
    this.updateLabelVisibility();
    this.applyHoleColors();
    // The overlay / cells signals may have settled before the layers existed (the effect already ran)
    this.renderCells();

    // Drawn contour (legacy layer). The same contour cuts the hole cells, see BlastHolesStateService.contour.
    this.polygonLayer
      .displayPolygonsForBlast([blastId], BLAST_POLYGON_TYPES, false, BLAST_POLYGON_STYLE)
      .catch(error => console.error('BlastHolesMap: unable to draw the blast polygon', error));
  }

  private resetLayers(): void {
    if (!this.map) return;
    this.holeLayer?.hideAllHoles();
    this.holeLayer?.clear();
    this.polygonLayer?.clear();
    this.cellsLayer?.clear();
    for (const layer of this.map.getLayers().getArray().slice()) {
      this.map.removeLayer(layer);
    }
    this.holeLayer    = null;
    this.polygonLayer = null;
    this.cellsLayer   = null;
  }

  // ── Geology cells ─────────────────────────────────────────────────────────

  private renderCells(): void {
    if (!this.cellsLayer || !this.siteProjection) return;
    const overlay = this.state.cellOverlay();
    if (!overlay) {
      this.cellsLayer.clear();
      return;
    }

    // Cells are computed in the site projection (shared with the Quality view); draw them in the map projection.
    const epsg = this.siteProjection;
    const cells: HoleCellFeature[] = [];
    for (const cell of this.state.holeCells().values()) {
      cells.push({
        id:    cell.id,
        color: overlay.fills.get(cell.id) ?? null,
        rings: cell.rings.map(ring => ring.map(coordinate => transform(coordinate, epsg, MapConstants.EPSG_REFERENCE_OL))),
      });
    }
    this.cellsLayer.update(cells);
  }

  private centerOn(x: number, y: number, zoom: number): void {
    if (!this.siteProjection) return;
    const center = transform([x, y], this.siteProjection, MapConstants.EPSG_REFERENCE_OL);
    this.view.setCenter(center);
    this.view.setZoom(zoom);
  }

  // ── Holes ─────────────────────────────────────────────────────────────────

  private scheduleRefresh(delay: number): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refreshHoles();
    }, delay);
  }

  private refreshHoles(): void {
    const blast = this.state.blast();
    if (!this.map || !this.holeLayer || !this.polygonLayer || !blast) return;

    const extent = this.view.calculateExtent(this.map.getSize());
    const blastIds = new Set<number>([blast.tirId]);
    for (const feature of this.polygonLayer.findPolygoneInView(extent as never)) {
      const id = feature.get(MapConstants.TIR_ID)?.value;
      if (typeof id === 'number') blastIds.add(id);
    }

    const filter = this.state.statusFilter();
    this.holeLayer.refreshHoles(
      [...blastIds],
      true,
      [blast.tirId],
      this.visibleStatuses(filter),
      filter as HoleStatus,
      null,
      blast,
      this.isMapLoading$,
      true,
      false,
    );
  }

  /** Pushes the view's per-hole colours (keyed by publicGuid) to the legacy layer (keyed by hole name). */
  private applyHoleColors(): void {
    if (!this.holeLayer) return;
    const colors = this.state.holeColors();
    if (!colors) {
      this.holeLayer.setColorOverride(null);
      return;
    }
    const byName = new Map<string, string | null>();
    for (const hole of this.state.holes()) {
      byName.set(hole.name, colors.get(hole.publicGuid) ?? null);
    }
    this.holeLayer.setColorOverride(byName);
  }

  private applyStatusFilter(filter: HoleStatusFilter): void {
    if (!this.holeLayer) return;
    this.holeLayer.refreshHoleVisibility(this.visibleStatuses(filter), filter as HoleStatus);
  }

  private visibleStatuses(filter: HoleStatusFilter): Set<HoleStatus> {
    if (filter !== 'actual') return new Set([filter]);
    return new Set(Object.values(HoleStatus).filter(status => status !== HoleStatus.UNSET));
  }

  private applySelection(holeId: string | null): void {
    if (!this.holeLayer) return;
    const hole = holeId ? this.state.holes().find(h => h.publicGuid === holeId) : undefined;
    this.holeLayer.setSelectedHole(hole?.name ?? null);
    this.holeLayer.refreshHoleStyles();

    if (hole?.PositionPlannedXYZ && this.siteProjection) {
      const center = transform(
        [hole.PositionPlannedXYZ.x, hole.PositionPlannedXYZ.y],
        this.siteProjection,
        MapConstants.EPSG_REFERENCE_OL,
      );
      this.view.animate({ center, duration: 500 });
    }
  }

  private onMapClick(pixel: Pixel): void {
    if (!this.map) return;
    const features = this.map.getFeaturesAtPixel(pixel) ?? [];
    const holeFeature = features.find(
      feature => feature.get(MapConstants.FEATURE_NAME) === MapConstants.FEATURE_TYPE.hole,
    );
    const hole = holeFeature?.get(MapConstants.HOLE_OBJECT);
    if (hole?.publicGuid) this.state.toggleHole(hole.publicGuid);
  }

  private updateLabelVisibility(): void {
    if (!this.holeLayer) return;
    const resolution = this.view.getResolution();
    this.holeLayer.setShowHoleNames(resolution !== undefined && resolution <= LABEL_HIDE_RESOLUTION);
  }

  // ── Graticule (site-projection coordinates on the axes) ───────────────────

  private createGraticule(projection: string): Graticule {
    const labelStyle = () => new OlText({ font: '12px Roboto', fill: new Fill({ color: '#5A5A5A' }) });
    return new Graticule({
      targetSize:        60,
      showLabels:        true,
      strokeStyle:       new Stroke({ color: 'rgba(126, 124, 124, 0.36)', width: 1 }),
      lonLabelFormatter: lon => this.formatAxisLabel(lon, true, projection),
      latLabelFormatter: lat => this.formatAxisLabel(lat, false, projection),
      lonLabelPosition:  0.15,
      latLabelPosition:  0.055,
      lonLabelStyle:     labelStyle(),
      latLabelStyle:     labelStyle(),
    });
  }

  private formatAxisLabel(value: number, isLongitude: boolean, projection: string): string {
    const center = this.view.getCenter();
    if (!center) return '';
    const centerWgs84 = transform(center, this.view.getProjection(), 'EPSG:4326');
    const point = isLongitude ? [value, centerWgs84[1]] : [centerWgs84[0], value];
    const inSiteProjection = transform(point, 'EPSG:4326', projection);
    return inSiteProjection[isLongitude ? 0 : 1].toFixed(0);
  }
}
